import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, GraduationCap, Gift, Sparkles, X, CreditCard } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import PayPalSubscribeButton from '@/components/PayPalSubscribeButton';
import useSEO from '@/hooks/useSEO';
import { getStashedAffiliateCode } from '@/components/AffiliateRefTracker';

const PLAN_IDS = {
    explorer: {
        monthly: 'P-8TJ038453D751554WNHTVLVA',
        annual: 'P-1SL503533G7497607NHTWCXY',
    },
    polyglot: {
        monthly: 'P-8DE21675BN0736521NHTVMFQ',
        annual: 'P-0U672812L2078024KNHTWDSY',
    },
    global: {
        monthly: 'P-60593354YP307293MNHTVIGQ',
        annual: 'P-7WT40919W43254141NHTWEIY',
    },
};

// PRIVATE — discounted plans for the affiliate / influencer program (10% off).
// These plan IDs are ONLY rendered when the visitor arrives via a validated
// ?ref=CODE link. The Pricing page guards on `affiliateInfo` being set, and
// the backend validates the code server-side via /affiliates/validate-code
// before we surface the discounted PayPal button. Direct navigation to
// /pricing without a code never exposes these plans.
const PLAN_IDS_AFFILIATE = {
    polyglot: {
        monthly: 'P-76516291AY6497301NI2DTYI',  // £4.04/mo
        annual:  'P-3FG64945DP031752HNI2DSJY',  // £44.45/yr
    },
    global: {
        monthly: 'P-6DG63424VA274212CNI2DQKA',  // £5.39/mo
        annual:  'P-1NF223119J043230YNI2DNDA',  // £59.30/yr
    },
};

// Discounted price labels shown on the card when a valid affiliate code is
// active. Kept in sync with the PayPal plan amounts above.
const AFFILIATE_PRICES = {
    polyglot: { monthly: 4.04, annual: 44.45 },
    global:   { monthly: 5.39, annual: 59.30 },
};

const TIERS = [
    { slug: 'free', name: 'Free', price: { monthly: 0, annual: 0 }, blurb: 'Try Linguistic Quest with your child.', cta: 'Start free', ctaHref: '/register', highlight: false,
      features: ['1 child profile, parent administers', 'Word Search in Shona only', 'All age tiers (4–16+)', 'No card required'] },
    { slug: 'explorer', name: 'Explorer', price: { monthly: 3.49, annual: 38.39 }, blurb: 'Parent plays too · one language.', highlight: false,
      features: ['Parent + up to 3 child profiles', '1 language of your choice', 'Every puzzle type, and anything we add', 'Family leaderboard · no ads'] },
    { slug: 'polyglot', name: 'Polyglot', price: { monthly: 4.49, annual: 49.39 }, blurb: 'Mixed-heritage families.', highlight: true,
      features: ['Parent + up to 4 child profiles', '2 languages', 'Every puzzle type, and anything we add', 'Vocabulary review + family leaderboard'] },
    { slug: 'global', name: 'Global', price: { monthly: 5.99, annual: 65.89 }, blurb: 'Serious learners.', highlight: false,
      features: ['Parent + up to 5 child profiles', 'All live languages', 'New languages as they launch', 'Priority support'] },
];

export default function Pricing() {
    useSEO({
        title: 'Pricing — Linguistic Quest plans (Explorer, Polyglot, Global)',
        description: 'Free for ever in Shona Word Search. Paid plans from £3.49/month: Explorer (1 language, 3 children), Polyglot (2 languages, 4 children), Global (every language, 5 children). 7-day free trial. Ad-free on every paid tier.',
        path: '/pricing',
    });
    const { user } = useAuth();
    const [period, setPeriod] = useState('monthly');
    const [err, setErr] = useState(null);
    // Affiliate banner — if the visitor came in via ?ref=CODE we validate
    // it and surface a 10% discount banner for Polyglot + Global tiers.
    // The actual discounted PayPal plan IDs need to be created in the PayPal
    // dashboard (one per tier × monthly/annual = 4 plans) — until then this
    // banner is informational only and the user still subscribes at full
    // price. Once the founder creates the discounted plans, swap the PLAN_IDS
    // map below to read from PLAN_IDS_AFFILIATE when `affiliateInfo` is set.
    const [affiliateInfo, setAffiliateInfo] = useState(null);
    useEffect(() => {
        // Check URL ?ref= first (covers the first-visit race where the global
        // tracker hasn't stashed yet) then fall back to localStorage for
        // returning visitors who hit /pricing days after first landing.
        const urlRef = new URLSearchParams(window.location.search).get('ref');
        const code = (urlRef || '').trim().toUpperCase() || getStashedAffiliateCode();
        if (!code) return;
        api.get('/affiliates/validate-code', { params: { code } })
            .then(({ data }) => setAffiliateInfo(data))
            .catch(() => setAffiliateInfo(null));
    }, []);

    const onPaypalApproved = async (tier, sub, billingPeriod) => {
        if (!user) {
            // Store then redirect to register; activation happens after account is created
            try {
                sessionStorage.setItem('lq_pending_subscription', JSON.stringify({
                    tier, billing_period: billingPeriod || period, subscription_id: sub.subscriptionID,
                }));
            } catch {}
            window.location.href = `${window.location.origin}/register?tier=${tier}&billing_period=${billingPeriod || period}&paypal_subscription_id=${sub.subscriptionID}`;
            return;
        }
        try {
            await api.post('/subscription/paypal/activate', {
                subscription_id: sub.subscriptionID,
                tier,
                billing_period: billingPeriod || period,
            });
            window.location.href = `${window.location.origin}/subscription/return?subscription_id=${sub.subscriptionID}`;
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not activate subscription.');
        }
    };

    const needsAccountFirst = !user;
    const [cardLoading, setCardLoading] = useState(null);
    const startCardCheckout = async (tier) => {
        setCardLoading(tier);
        setErr(null);
        try {
            const { data } = await api.post('/subscription/stripe/checkout', {
                tier, billing_period: period, origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not start card checkout, please try again.');
            setCardLoading(null);
        }
    };

    return (
        <main className="max-w-7xl mx-auto px-6 py-16" data-testid="page-pricing">
            {affiliateInfo && (
                <div className="mb-8 mx-auto max-w-3xl bg-gradient-to-r from-lq-purple-500 to-lq-purple-700 text-white rounded-2xl px-5 py-4 flex items-center gap-3 shadow-lg" data-testid="affiliate-banner">
                    <Sparkles size={20} className="flex-shrink-0"/>
                    <div className="flex-1 text-sm">
                        <strong>{affiliateInfo.affiliate_name} sent you!</strong> Use code <code className="font-mono bg-white/15 px-2 py-0.5 rounded">{affiliateInfo.code}</code> at checkout for <strong>{affiliateInfo.discount_pct}% off</strong> Polyglot or Global. <span className="opacity-80">(Explorer and Free tier excluded.)</span>
                    </div>
                </div>
            )}
            <div className="text-center">
                <p className="text-sm font-semibold uppercase tracking-wider text-lq-purple-500">Pricing</p>
                <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">Fluency decoded.</h1>
                <p className="mt-4 max-w-2xl mx-auto text-lq-ink-700">All prices in British Pounds (GBP). Pay by debit/credit card (no account needed) or PayPal. 7-day free trial on every paid tier. Cancel anytime.</p>
                <p className="mt-2 text-xs text-lq-success font-semibold" data-testid="pricing-ad-free-note">Paid plans are 100% ad-free. Free plan plays 3 short ads to unlock 3 more turns on a puzzle.</p>
                <div className="mt-6 inline-flex bg-lq-purple-100 p-1 rounded-full" role="radiogroup" aria-label="Billing period">
                    <button onClick={() => setPeriod('monthly')} className={`px-5 py-2 rounded-full text-sm font-semibold ${period === 'monthly' ? 'bg-white text-lq-purple-900 shadow-sm' : 'text-lq-purple-700'}`} data-testid="pricing-toggle-monthly" role="radio" aria-checked={period === 'monthly'}>Monthly</button>
                    <button onClick={() => setPeriod('annual')} className={`px-5 py-2 rounded-full text-sm font-semibold ${period === 'annual' ? 'bg-white text-lq-purple-900 shadow-sm' : 'text-lq-purple-700'}`} data-testid="pricing-toggle-annual" role="radio" aria-checked={period === 'annual'}>Annual <span className="text-lq-peach-600 ml-1">save 17%</span></button>
                </div>
                {period === 'annual' && (
                    <p className="mt-3 text-xs text-lq-ink-500 max-w-lg mx-auto" data-testid="pricing-annual-note">Annual plans save ~17% — subscribe in one click via PayPal.</p>
                )}
            </div>

            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                {TIERS.map((t) => {
                    // Eligibility gate: discounted plans only render when a
                    // valid affiliate code is in scope AND the tier is on the
                    // eligible list (polyglot / global). Otherwise we always
                    // fall back to the full-price PLAN_IDS.
                    const isAffEligible = !!(affiliateInfo
                        && (affiliateInfo.eligible_tiers || []).includes(t.slug)
                        && PLAN_IDS_AFFILIATE[t.slug]);
                    const planId = t.slug === 'free' ? null
                        : isAffEligible ? PLAN_IDS_AFFILIATE[t.slug][period]
                        : PLAN_IDS[t.slug]?.[period];
                    const fullPrice = t.price[period];
                    const affPrice = isAffEligible ? AFFILIATE_PRICES[t.slug][period] : null;
                    const displayPrice = affPrice ?? fullPrice;
                    return (
                        <div key={t.slug} className={`relative flex flex-col rounded-2xl p-6 border-2 bg-white transition-all ${t.highlight ? 'border-lq-purple-500 shadow-xl -translate-y-1' : 'border-lq-ink-100'} ${isAffEligible ? 'ring-2 ring-lq-purple-300 ring-offset-2' : ''}`} data-testid={`pricing-tier-${t.slug}`}>
                            {t.highlight && <span className="absolute -top-3 left-6 bg-lq-purple-500 text-white text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full">Most popular</span>}
                            {isAffEligible && <span className="absolute -top-3 right-6 bg-lq-success text-white text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full" data-testid={`pricing-aff-pill-${t.slug}`}>10% off</span>}
                            <h3 className="font-display text-2xl font-semibold text-lq-ink-900">{t.name}</h3>
                            <p className="text-sm text-lq-ink-500 mt-1">{t.blurb}</p>
                            <div className="mt-5">
                                {isAffEligible && (
                                    <span className="text-lq-ink-500 text-base mr-2 line-through" data-testid={`pricing-strike-${t.slug}`}>£{fullPrice.toFixed(2)}</span>
                                )}
                                <span className={`font-display text-4xl font-semibold ${isAffEligible ? 'text-lq-success' : 'text-lq-ink-black'}`} data-testid={`pricing-display-price-${t.slug}`}>£{displayPrice.toFixed(displayPrice === 0 ? 0 : 2)}</span>
                                <span className="text-lq-ink-500 text-sm ml-1">/ {period === 'monthly' ? 'mo' : 'yr'}</span>
                            </div>
                            <ul className="mt-5 space-y-2 text-sm text-lq-ink-700 flex-1">
                                {t.features.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="mt-0.5 text-lq-success flex-shrink-0"/> {f}</li>)}
                            </ul>

                            <div className="mt-6">
                                {t.slug === 'free' && (
                                    <Link to="/register" className="block text-center rounded-full font-semibold py-2.5 bg-lq-ink-100 text-lq-ink-900 hover:bg-lq-purple-100" data-testid="pricing-cta-free">Start free</Link>
                                )}
                                {t.slug !== 'free' && needsAccountFirst && (
                                    <Link to={`/register?tier=${t.slug}&period=${period}${isAffEligible ? `&ref=${affiliateInfo.code}` : ''}`} className={`block text-center rounded-full font-semibold py-2.5 transition-colors ${t.highlight ? 'bg-lq-purple-500 text-white hover:bg-lq-purple-700' : 'bg-lq-ink-100 text-lq-ink-900 hover:bg-lq-purple-100'}`} data-testid={`pricing-cta-${t.slug}`}>Create account first</Link>
                                )}
                                {t.slug !== 'free' && !needsAccountFirst && (
                                    <>
                                        <button
                                            onClick={() => startCardCheckout(t.slug)}
                                            disabled={!!cardLoading}
                                            className={`w-full mb-1 inline-flex items-center justify-center gap-2 rounded-full font-semibold py-2.5 transition-colors ${t.highlight ? 'bg-lq-purple-500 text-white hover:bg-lq-purple-700' : 'bg-lq-ink-900 text-white hover:bg-lq-ink-700'} disabled:opacity-60`}
                                            data-testid={`pricing-card-cta-${t.slug}`}
                                        >
                                            <CreditCard size={16} />
                                            {cardLoading === t.slug ? 'Opening secure checkout…' : 'Subscribe with card'}
                                        </button>
                                        <p className="text-[11px] text-center text-lq-ink-500 mb-2">No PayPal account needed · Apple&nbsp;Pay / Google&nbsp;Pay</p>
                                        {planId
                                            ? <PayPalSubscribeButton planId={planId} onApprove={(d) => onPaypalApproved(t.slug, d)} onError={() => setErr('PayPal error, please try again.')} />
                                            : <div className="text-center text-xs text-lq-ink-500 py-2" data-testid={`pricing-coming-soon-${t.slug}`}>Annual plan coming soon</div>}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {err && <p className="mt-3 text-center text-sm text-lq-error" data-testid="pricing-error">{err}</p>}

            {/* Payment methods card */}
            <section className="mt-16 grid md:grid-cols-3 gap-5">
                <div className="bg-white border border-lq-ink-100 rounded-2xl p-6">
                    <h3 className="font-display text-lg font-semibold text-lq-ink-900">Pay by card or PayPal</h3>
                    <p className="mt-2 text-sm text-lq-ink-500">Debit/credit card via Stripe (no account needed, Apple Pay and Google Pay supported) or subscribe with PayPal. Your card never touches our servers.</p>
                </div>
                <div className="bg-white border border-lq-ink-100 rounded-2xl p-6">
                    <div className="flex items-center gap-2"><Gift size={18} className="text-lq-peach-600"/><h3 className="font-display text-lg font-semibold text-lq-ink-900">Bought a gift card?</h3></div>
                    <p className="mt-2 text-sm text-lq-ink-500">Cash, bank transfer, or mobile money: pay at one of our partners, get a voucher, redeem the code online.</p>
                    <Link to="/redeem" className="mt-3 inline-flex text-sm font-semibold text-lq-purple-700 hover:underline" data-testid="pricing-redeem-link">Redeem a code &rarr;</Link>
                </div>
                <div className="bg-white border border-lq-ink-100 rounded-2xl p-6">
                    <div className="flex items-center gap-2"><GraduationCap size={18} className="text-lq-sky-600"/><h3 className="font-display text-lg font-semibold text-lq-ink-900">Schools and districts</h3></div>
                    <p className="mt-2 text-sm text-lq-ink-500">Per-seat pricing, invoiced by bank transfer or PayPal Invoicing, net 30 terms.</p>
                    <Link to="/schools" className="mt-3 inline-flex text-sm font-semibold text-lq-purple-700 hover:underline">Get a quote &rarr;</Link>
                </div>
            </section>
        </main>
    );
}
